import React from 'react';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import { ReactComponent as CheckIcon } from '../../image/Check3.svg';

export default function MembershipSelectPanel(props) {
  const {
    memberships,
    categories,
    isAllSelected,
    selectedIds,
    onClickAll,
    onClickItem
  } = props;

  const { t } = useLanguage();

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  // 카테고리 순서대로 회원권 그룹핑 — 카테고리 미매칭 항목은 헤더 없이 말미에 표시 //
  const safeMemberships = Array.isArray(memberships) ? memberships : [];
  const safeCategories = Array.isArray(categories) ? categories : [];

  const groups = safeCategories
    .map((cat) => ({
      categoryId: cat.id,
      categoryName: cat.name,
      items: safeMemberships.filter((m) => m.categoryId === cat.id)
    }))
    .filter((g) => g.items.length > 0);

  const categorizedIds = new Set(groups.flatMap((g) => g.items.map((m) => m.id)));
  const uncategorized = safeMemberships.filter((m) => !categorizedIds.has(m.id));

  // 개별 행 렌더 //
  function renderMembershipRow(m) {
    const isOn = isAllSelected || selectedIds.includes(m.id);

    return (
      <ItemRow key={'Mem-' + m.id} onClick={() => onClickItem(m.id)}>
        <LabelWrapper>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{m.name}</Text>
        </LabelWrapper>
        <CheckBox isOn={isOn}>
          {
            isOn &&
            <CheckSvg />
          }
        </CheckBox>
      </ItemRow>
    );
  }

  return (
    <PanelWrapper>
      <HeaderRow>
        <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text}>{t('myStoreSetting.terms.applyMembership')}</Text>
      </HeaderRow>
      <DivisionLine backgroundColor={theme.line} />
      <ItemRow onClick={onClickAll}>
        <LabelWrapper>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('myStoreSetting.terms.allMemberships')}</Text>
        </LabelWrapper>
        <CheckBox isOn={isAllSelected}>
          {
            isAllSelected &&
            <CheckSvg />
          }
        </CheckBox>
      </ItemRow>
      <ListScroll>
        {
          groups.map((g) => (
            <GroupWrapper key={'MemCat-' + g.categoryId}>
              <CategoryRow>
                <LabelWrapper>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{g.categoryName}</Text>
                </LabelWrapper>
              </CategoryRow>
              {g.items.map(renderMembershipRow)}
            </GroupWrapper>
          ))
        }
        {uncategorized.map(renderMembershipRow)}
      </ListScroll>
    </PanelWrapper>
  );
}

const PanelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  padding: 10px 10px 10px 15px;

  width: 209px;
  height: 100%;

  gap: 10px;

  border: 1px solid ${props => props.theme.line};
  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};

  box-sizing: border-box;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0;

  color: ${props => props.color};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  padding: 8px 0;

  width: 100%;
`;

const LabelWrapper = styled.div`
  flex: 1 0 0;
  min-width: 0;
`;

const DivisionLine = styled.div`
  width: 100%;
  height: 2px;

  background-color: ${props => props.backgroundColor};

  opacity: 0.3;
`;

const ItemRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: calc(100% - 5px);
  height: 28px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const GroupWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  width: 100%;

  gap: 4px;
`;

const CategoryRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  padding-top: 8px;

  width: 100%;
  height: 24px;
`;

const CheckBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 20px;
  height: 20px;
  
  margin-right: 5px;

  border: 1px solid ${props => props.isOn ? `transparent` : props.theme.line};
  border-radius: 5px;
  background-color: ${props => props.isOn ? props.theme.primary : `transparent`};

  box-sizing: border-box;
  flex-shrink: 0;

  transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
`;

const CheckSvg = styled(CheckIcon)`
  width: 10px;
  height: 8px;
`;

const ListScroll = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  width: 100%;
  flex: 1 0 0;
  min-height: 0;

  gap: 4px;

  overflow-x: hidden;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 1px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.primary};
    border-radius: 200px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;
